import { useState, useEffect } from 'react'
import { Hash, RefreshCw } from 'lucide-react'

interface ICCounterData {
  current_value: number
  next_ic: string
  prefix: string
  updated_at: string
}

export default function ICCounterCard() {
  const [counter, setCounter] = useState<ICCounterData | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchCounter()
  }, [])

  const fetchCounter = async () => {
    try {
      const response = await fetch('/api/ic-counter')
      const data = await response.json()
      if (data.success) {
        setCounter(data)
        setInputValue(data.current_value.toString())
      }
    } catch (err) {
      console.error('Error fetching IC counter:', err)
      setError('Failed to load counter')
    }
  }

  const handleUpdate = async () => {
    const value = parseInt(inputValue)

    if (isNaN(value) || value < 1) {
      setError('Please enter a valid positive number')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await fetch('/api/ic-counter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value })
      })

      const data = await response.json()

      if (data.success) {
        setCounter(data)
        setInputValue(data.current_value.toString())

        // Show success toast (you can integrate with your toast system)
        alert(`IC counter updated to ${value}. Next IC: ${data.next_ic}`)
      } else {
        setError(data.error || 'Failed to update counter')
      }
    } catch (err) {
      console.error('Error updating IC counter:', err)
      setError('Failed to update counter')
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleUpdate()
    }
  }

  if (!counter) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #1A1F2E, #242938)',
        border: '2px solid #9D4EDD',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 0 20px rgba(157, 78, 221, 0.2)',
        minHeight: '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <RefreshCw style={{ width: 24, height: 24, color: '#9D4EDD', animation: 'spin 1s linear infinite' }} />
      </div>
    )
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1A1F2E, #242938)',
      border: '2px solid #9D4EDD',
      borderRadius: '12px',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 0 20px rgba(157, 78, 221, 0.2)'
    }}>
      {/* Corner accent */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100px',
        height: '100px',
        background: 'linear-gradient(135deg, transparent 50%, rgba(157, 78, 221, 0.2) 50%)',
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div style={{
          background: 'rgba(157, 78, 221, 0.2)',
          border: '2px solid #9D4EDD',
          borderRadius: '8px',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Hash style={{ width: 24, height: 24, color: '#9D4EDD' }} />
        </div>
        <h3 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '20px',
          fontWeight: 700,
          color: '#9D4EDD',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          margin: 0,
          textShadow: '0 0 10px rgba(157, 78, 221, 0.6)'
        }}>
          IC Counter
        </h3>
      </div>

      {/* Current Value Display */}
      <div style={{
        background: 'rgba(157, 78, 221, 0.1)',
        border: '1px solid rgba(157, 78, 221, 0.3)',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div style={{
          fontSize: '11px',
          color: '#8A95AA',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          fontWeight: 600,
          marginBottom: '8px'
        }}>
          Current Counter
        </div>
        <div style={{
          fontSize: '32px',
          fontWeight: 700,
          color: '#9D4EDD',
          fontFamily: 'Rajdhani, sans-serif',
          textShadow: '0 0 10px rgba(157, 78, 221, 0.4)'
        }}>
          {counter.current_value}
        </div>
      </div>

      {/* Next IC Preview */}
      <div style={{
        background: 'rgba(0, 240, 255, 0.1)',
        border: '1px solid rgba(0, 240, 255, 0.3)',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '20px'
      }}>
        <div style={{
          fontSize: '11px',
          color: '#8A95AA',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          fontWeight: 600,
          marginBottom: '4px'
        }}>
          Next IC
        </div>
        <div style={{
          fontSize: '18px',
          fontWeight: 700,
          color: '#00F0FF',
          fontFamily: 'Rajdhani, sans-serif'
        }}>
          {counter.next_ic}
        </div>
      </div>

      {/* Input Section */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '11px',
          color: '#8A95AA',
          letterSpacing: '1px',
          marginBottom: '8px',
          textTransform: 'uppercase',
          fontWeight: 600
        }}>
          Set Counter Value
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              const val = e.target.value
              if (val === '' || /^\d+$/.test(val)) {
                setInputValue(val)
                setError('')
              }
            }}
            onKeyPress={handleKeyPress}
            placeholder="Enter number"
            disabled={loading}
            style={{
              flex: 1,
              height: '44px',
              padding: '12px 16px',
              background: 'rgba(26, 31, 46, 0.6)',
              border: '2px solid #9D4EDD',
              borderRadius: '6px',
              color: '#E0E6F0',
              fontSize: '14px',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: 600,
              outline: 'none',
              transition: 'all 0.2s',
              boxShadow: '0 0 10px rgba(157, 78, 221, 0.2)'
            }}
          />
          <button
            onClick={handleUpdate}
            disabled={loading}
            style={{
              padding: '12px 24px',
              background: loading ? 'rgba(157, 78, 221, 0.3)' : 'linear-gradient(135deg, #9D4EDD, #7B2CBF)',
              border: '2px solid #9D4EDD',
              borderRadius: '6px',
              color: '#E0E6F0',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 700,
              fontSize: '14px',
              textTransform: 'uppercase',
              fontFamily: 'Rajdhani, sans-serif',
              boxShadow: loading ? 'none' : '0 0 20px rgba(157, 78, 221, 0.4)',
              transition: 'all 0.2s',
              opacity: loading ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.boxShadow = '0 0 30px rgba(157, 78, 221, 0.6)'
                e.currentTarget.style.transform = 'translateY(-2px)'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 0 20px rgba(157, 78, 221, 0.4)'
              e.currentTarget.style.transform = 'translateY(0)'
            }}
          >
            {loading ? 'Updating...' : 'Update'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '12px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '6px',
          color: '#EF4444',
          fontSize: '13px',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {/* Info */}
      <div style={{
        padding: '12px',
        background: 'rgba(0, 240, 255, 0.05)',
        border: '1px solid rgba(0, 240, 255, 0.2)',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#8A95AA',
        marginBottom: '12px'
      }}>
        ℹ️ System automatically skips already-used ICs when generating new items
      </div>

      {/* Last Updated */}
      <div style={{
        fontSize: '11px',
        color: '#8A95AA',
        textAlign: 'center'
      }}>
        Last Updated: {new Date(counter.updated_at).toLocaleString()}
      </div>
    </div>
  )
}
